
import React from 'react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import ClientsSection from '@/components/ClientsSection';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowRight, Users, Search, Bot, Building, FileCheck, Star } from 'lucide-react';
import { Link } from 'react-router-dom';

const TalentRecruitment = () => {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      <section className="py-20 bg-gradient-to-br from-white via-[#F0F9FF] to-[#E0F2FE]">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <h1 className="text-4xl font-bold mb-6 tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-[#0EA5E9] to-[#6366F1]">
              Talent Recruitment
            </h1>
            <p className="text-lg text-gray-700">
              We help companies find the right talent who can work effectively with AI agents, 
              creating hybrid human-AI teams that deliver exceptional results.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
            <div>
              <h2 className="text-3xl font-bold mb-6 text-[#0EA5E9]">For Companies</h2>
              <p className="text-gray-700 mb-6">
                Finding talent who can work effectively with AI is challenging. We help you identify, 
                assess, and hire professionals who can thrive in AI-augmented roles.
              </p>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="bg-blue-100 w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <Search className="w-5 h-5 text-[#0EA5E9]" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2">AI-Ready Talent Sourcing</h3>
                    <p className="text-gray-700">
                      We identify candidates who have the skills and mindset to work effectively with AI agents.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="bg-blue-100 w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <FileCheck className="w-5 h-5 text-[#0EA5E9]" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2">AI Competency Assessment</h3>
                    <p className="text-gray-700">
                      We evaluate candidates' ability to collaborate with AI systems and leverage their capabilities.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="bg-blue-100 w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <Building className="w-5 h-5 text-[#0EA5E9]" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2">Hybrid Team Building</h3>
                    <p className="text-gray-700">
                      We help you build effective teams that combine human talent with AI capabilities.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="mt-8">
                <Link to="/join-waitlist">
                  <Button className="bg-gradient-to-r from-[#0EA5E9] to-[#3B82F6] hover:from-[#0EA5E9]/90 hover:to-[#3B82F6]/90">
                    Hire AI-Ready Talent
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
            </div>
            
            <div>
              <h2 className="text-3xl font-bold mb-6 text-[#0EA5E9]">For Talent</h2>
              <p className="text-gray-700 mb-6">
                As AI transforms the workplace, professionals who can effectively collaborate with AI will be in high demand. 
                We help you position yourself for these exciting opportunities.
              </p>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="bg-blue-100 w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <Bot className="w-5 h-5 text-[#0EA5E9]" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2">AI-Augmented Roles</h3>
                    <p className="text-gray-700">
                      Access opportunities where you work alongside AI agents, focusing on high-value work.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="bg-blue-100 w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <Star className="w-5 h-5 text-[#0EA5E9]" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2">Skill Enhancement</h3>
                    <p className="text-gray-700">
                      Develop the skills needed to thrive in the AI-augmented workplace.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="bg-blue-100 w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <Users className="w-5 h-5 text-[#0EA5E9]" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2">Career Advancement</h3>
                    <p className="text-gray-700">
                      Position yourself for high-growth roles in the AI-augmented economy.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="mt-8">
                <Link to="/">
                  <Button className="bg-gradient-to-r from-[#0EA5E9] to-[#3B82F6] hover:from-[#0EA5E9]/90 hover:to-[#3B82F6]/90">
                    Submit Your Resume
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
          
          <div className="max-w-3xl mx-auto mt-20">
            <h2 className="text-3xl font-bold mb-8 text-center text-[#0EA5E9]">Our Recruitment Process</h2>
            
            <div className="space-y-6">
              <div className="bg-white p-6 rounded-lg shadow-md">
                <div className="flex flex-col md:flex-row gap-4 items-center">
                  <div className="bg-blue-100 w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-xl font-bold text-[#0EA5E9]">1</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2">Talent Assessment</h3>
                    <p className="text-gray-700">
                      We evaluate candidates based on their technical skills, AI readiness, and ability to work in hybrid teams.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow-md">
                <div className="flex flex-col md:flex-row gap-4 items-center">
                  <div className="bg-blue-100 w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-xl font-bold text-[#0EA5E9]">2</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2">AI Compatibility Matching</h3>
                    <p className="text-gray-700">
                      We match candidates with AI agents that complement their skills and work styles.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow-md">
                <div className="flex flex-col md:flex-row gap-4 items-center">
                  <div className="bg-blue-100 w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-xl font-bold text-[#0EA5E9]">3</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2">Company Matching</h3>
                    <p className="text-gray-700">
                      We connect candidates with companies looking for AI-ready talent in their field.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow-md">
                <div className="flex flex-col md:flex-row gap-4 items-center">
                  <div className="bg-blue-100 w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-xl font-bold text-[#0EA5E9]">4</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2">Onboarding Support</h3>
                    <p className="text-gray-700">
                      We provide training and support to help new hires and their AI agents work effectively together.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Add the Clients Section */}
      <ClientsSection />
      
      <Footer />
    </div>
  );
};

export default TalentRecruitment;
